// Telegram ops-chat binding API (Wave 9.3) — typed functions over the shipped
// Wave 9.1 admin backend contract. Consumed verbatim; no invented fields.
//
//   POST   /admin/telegram/validate-chat            — DRY-RUN liveness check
//   PUT    /admin/agencies/{id}/telegram-chat       — bind (server re-validates)
//   PUT    /admin/clients/{id}/telegram-chat        — bind (server re-validates)
//   PUT    /admin/ops-chats/admin                   — bind the admin singleton
//   DELETE (same three paths)                       — unbind (204; 404 = none)
//   GET    /admin/ops-chats                         — all bindings, active + not
//
// The backend answers refusals as STRUCTURED bodies ({ok:false, reason, hint?,
// bound_kind?}) on 409/422/502/503. Those are expected outcomes the UI renders,
// not exceptions — so every function here returns a discriminated union and
// only throws for genuinely unexpected failures (401 handling, network, 5xx
// without a structured body, ...).
//
// chat_id facts: Telegram chat ids are 64-bit integers but stay within 52
// significant bits, so a plain JS number is safe; groups are NEGATIVE
// (supergroups look like -1001234567890). Sent as a JSON number.

import { apiFetch, type ApiError } from "./client";

export type BindingKind = "client" | "agency" | "admin";
export type BotStatus = "administrator" | "member";

// Per-chat notification language (Wave 9.7). The bot renders ops/notification
// messages for a bound chat in this language.
export type ChatLanguage = "ru" | "en" | "zh";

// Kind default when a binding carries no explicit language — client chats
// default to Russian, agency/admin chats to English. Used both for display of
// an empty binding.language AND sent explicitly on a fresh bind.
export const LANGUAGE_DEFAULT: Record<BindingKind, ChatLanguage> = {
  client: "ru",
  agency: "en",
  admin: "en",
};

export type ChatRefusalReason =
  | "ops_bot_not_configured"
  | "telegram_unavailable"
  | "chat_not_found"
  | "not_a_group"
  | "privacy_mode_on"
  | "bot_not_in_chat"
  | "chat_already_bound";

// Success body of the dry-run validate call.
export interface ChatValidationOk {
  ok: true;
  chat_id: number;
  chat_title: string;
  chat_type: string;
  bot_status: BotStatus;
}

// A structured refusal (409 / 422 / 502 / 503). `hint` is server-authored and
// shown verbatim; `bound_kind` is set only for chat_already_bound.
export interface ChatRefusal {
  ok: false;
  status: number;
  reason: ChatRefusalReason;
  hint?: string;
  bound_kind?: BindingKind;
}

export type ValidateChatResult = ChatValidationOk | ChatRefusal;

// One binding — the PUT success `binding` object and the GET /admin/ops-chats
// rows share this shape (the GET rows may omit title/bot_status/validated_at).
export interface ChatBinding {
  chat_id: number;
  kind: BindingKind;
  client_id?: string;
  agency_id?: string;
  title?: string;
  active: boolean;
  bot_status?: BotStatus;
  // Per-chat notification language; may be empty, in which case the UI falls
  // back to LANGUAGE_DEFAULT[kind] for display.
  language?: ChatLanguage;
  // ISO-8601 timestamp of the last successful live validation.
  validated_at?: string;
  // Unix seconds.
  created_at: number;
}

export interface BindChatOk {
  ok: true;
  binding: ChatBinding;
}

export type BindChatResult = BindChatOk | ChatRefusal;

export interface OpsChatList {
  chats: ChatBinding[];
}

// Convert a thrown ApiError carrying a structured {ok:false, reason} body into
// a ChatRefusal, or null when it is not one (caller rethrows).
function asRefusal(err: unknown): ChatRefusal | null {
  const e = err as ApiError;
  if (!e || typeof e.status !== "number") return null;
  if (![409, 422, 502, 503].includes(e.status)) return null;
  const b = e.body as
    | { ok?: unknown; reason?: unknown; hint?: unknown; bound_kind?: unknown }
    | undefined;
  if (!b || b.ok !== false || typeof b.reason !== "string") return null;
  return {
    ok: false,
    status: e.status,
    reason: b.reason as ChatRefusalReason,
    hint: typeof b.hint === "string" ? b.hint : undefined,
    bound_kind:
      typeof b.bound_kind === "string"
        ? (b.bound_kind as BindingKind)
        : undefined,
  };
}

// The PUT/DELETE path for one binding owner.
function bindingPath(kind: BindingKind, ownerId?: string): string {
  switch (kind) {
    case "agency":
      return `/admin/agencies/${ownerId}/telegram-chat`;
    case "client":
      return `/admin/clients/${ownerId}/telegram-chat`;
    case "admin":
      return "/admin/ops-chats/admin";
  }
}

// Dry-run: is the bot actually usable in this chat? Never mutates anything.
export async function validateChat(chatId: number): Promise<ValidateChatResult> {
  try {
    return await apiFetch<ChatValidationOk>("/admin/telegram/validate-chat", {
      method: "POST",
      body: { chat_id: chatId },
    });
  } catch (err) {
    const refusal = asRefusal(err);
    if (refusal) return refusal;
    throw err;
  }
}

// Bind a chat to an owner. The backend ALWAYS re-validates live before saving,
// so this can return every validate refusal plus chat_already_bound (409). The
// language is sent explicitly (default LANGUAGE_DEFAULT[kind]) so a fresh bind
// stores the kind default rather than leaving it implicit.
export async function bindChat(
  kind: BindingKind,
  ownerId: string | undefined,
  chatId: number,
  language: ChatLanguage = LANGUAGE_DEFAULT[kind],
): Promise<BindChatResult> {
  try {
    return await apiFetch<BindChatOk>(bindingPath(kind, ownerId), {
      method: "PUT",
      body: { chat_id: chatId, language },
    });
  } catch (err) {
    const refusal = asRefusal(err);
    if (refusal) return refusal;
    throw err;
  }
}

// The language-update path for one binding owner. Client/agency are POST to a
// sub-resource; the admin singleton is a PUT (matching the bind verb).
function languagePath(kind: BindingKind, ownerId?: string): string {
  switch (kind) {
    case "agency":
      return `/admin/agencies/${ownerId}/telegram-chat/language`;
    case "client":
      return `/admin/clients/${ownerId}/telegram-chat/language`;
    case "admin":
      return "/admin/ops-chats/admin/language";
  }
}

// Set the per-chat notification language on an already-bound owner. Throws on
// any failure (the caller renders an error toast + rolls back the optimistic
// update); the backend authorizes the write.
export async function setChatLanguage(
  kind: BindingKind,
  ownerId: string | undefined,
  language: ChatLanguage,
): Promise<void> {
  await apiFetch(languagePath(kind, ownerId), {
    method: kind === "admin" ? "PUT" : "POST",
    body: { language },
  });
}

export function putAgencyChat(agencyId: string, chatId: number) {
  return bindChat("agency", agencyId, chatId);
}

export function putClientChat(clientId: string, chatId: number) {
  return bindChat("client", clientId, chatId);
}

export function putAdminChat(chatId: number) {
  return bindChat("admin", undefined, chatId);
}

// Unbind. 204 = unbound; 404 (nothing bound) is treated as already-done, not
// an error — the end state is identical.
export async function unbindChat(
  kind: BindingKind,
  ownerId?: string,
): Promise<void> {
  try {
    await apiFetch(bindingPath(kind, ownerId), { method: "DELETE" });
  } catch (err) {
    if ((err as ApiError)?.status === 404) return;
    throw err;
  }
}

export function deleteAgencyChat(agencyId: string) {
  return unbindChat("agency", agencyId);
}

export function deleteClientChat(clientId: string) {
  return unbindChat("client", clientId);
}

export function deleteAdminChat() {
  return unbindChat("admin");
}

// All bindings (active AND inactive rows).
export function listOpsChats(): Promise<OpsChatList> {
  return apiFetch<OpsChatList>("/admin/ops-chats", { method: "GET" });
}

// ---- Money-approval approvers (Telegram Login Widget) -----------------------
//
// The reviewed Telegram accounts allowed to CONFIRM money (recharge) actions
// from the ops bot. The gate arms automatically once at least one is linked.
// Linking uses the Telegram Login Widget: Telegram signs a user object with the
// bot token; the FE posts it VERBATIM and the backend re-verifies the HMAC
// (single sanitized reason on failure — the hash/token never round-trip).
//
//   GET    /admin/telegram/config           — { bot_username } for the widget
//   POST   /admin/telegram/link             — widget user VERBATIM -> approver
//   GET    /admin/telegram/approvers        — active approvers, safe fields only
//   DELETE /admin/telegram/approvers/{id}   — remove one (204; 404 = unknown)

// The Telegram Login Widget bot username (empty string when unset server-side).
export interface TelegramConfig {
  bot_username: string;
}

// The Telegram Login Widget user object, posted VERBATIM. `id`/`auth_date` are
// JSON numbers; the rest strings. Optional fields are omitted (or "") when the
// widget did not supply them — the backend drops empties before the HMAC check,
// matching Telegram.
export interface TelegramWidgetUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

// A linked, verified money-approval approver. Safe fields only — no hash/token.
export interface TelegramApprover {
  id: string;
  telegram_id: number;
  username: string;
  first_name: string;
  // Unix seconds of the last successful verification.
  verified_at: number;
  // The admin portal_user id that linked this approver.
  linked_by: string;
}

export interface TelegramApproverList {
  approvers: TelegramApprover[];
}

// Fetch the widget bot username. `bot_username` is "" when TELEGRAM_BOT_USERNAME
// is unset on the server — the caller renders a "not configured" note instead
// of the widget.
export function getTelegramConfig(): Promise<TelegramConfig> {
  return apiFetch<TelegramConfig>("/admin/telegram/config", { method: "GET" });
}

// Link a Telegram account as a money-approval approver from the widget's signed
// user object. Posted VERBATIM; the backend verifies the HMAC. A 400
// {reason:"telegram_verification_failed"} (tampered/stale/wrong-token) surfaces
// as a sanitized ApiError to the caller — the hash is never echoed.
export function linkApprover(
  user: TelegramWidgetUser,
): Promise<TelegramApprover> {
  return apiFetch<TelegramApprover>("/admin/telegram/link", {
    method: "POST",
    body: user,
  });
}

// Active approvers, most-recently-verified first (backend-ordered).
export function listApprovers(): Promise<TelegramApproverList> {
  return apiFetch<TelegramApproverList>("/admin/telegram/approvers", {
    method: "GET",
  });
}

// Remove an approver. 204 = removed; 404 (already gone) is treated as done, not
// an error — the end state is identical.
export async function unlinkApprover(id: string): Promise<void> {
  try {
    await apiFetch(`/admin/telegram/approvers/${id}`, { method: "DELETE" });
  } catch (err) {
    if ((err as ApiError)?.status === 404) return;
    throw err;
  }
}
